/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { createContext, ReactNode, useContext, useMemo, useRef, useState, SyntheticEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isDateEmptyOrNull,
  isStrEmptyOrNull,
  isValidEmailAddress,
  isValidLoanNo,
  isValidPassword,
  isValidReenterField,
} from '../common/UtilityFunctions';
import axiosCall from '../services/axios';
import useAbortController from '../common/hooks/useAbortController';
import dayjs, { Dayjs } from 'dayjs';
import { useLoginAuditContext } from './LoginAuditContext';

type statusAlertType = {
  status: string;
  message: string;
};

type resetPasswordValue = {
  status: string;
  statusAlert: statusAlertType;
  setStatusAlert: (statusAlertType) => void;
  loadingMessage: string;
  handleFirstSubmit: () => void;
  handleFirstSuccess: () => void;
  handleSuccess: () => void;
  handleError: (string) => void;
  validateFields: (SynetheticEvent) => void;
  validateSecretQuestionFields: () => void;

  resetPasswordModalOpen: boolean;
  setResetPasswordModalOpen: (boolean) => void;
  passwordSecretQuestionModalOpen: boolean;
  setPasswordSecretQuestionModalOpen: (boolean) => void;
  emailAddressError: boolean;
  setEmailAddressError: (boolean) => void;
  emailAddressErrorMsg: string;
  setEmailAddressErrorMsg: (string) => void;
  secretAnswerError: boolean;
  setSecretAnswerError: (boolean) => void;
  secretAnswerErrorMsg: string;
  setSecretAnswerErrorMsg: (string) => void;
  passwordError: boolean;
  setPasswordError: (boolean) => void;
  passwordErrorMsg: string;
  setPasswordErrorMsg: (string) => void;
  reenterPasswordError: boolean;
  setReenterPasswordError: (boolean) => void;
  reenterPasswordErrorMsg: string;
  setReenterPasswordErrorMsg: (string) => void;
  reenterPasswordChanged: boolean;
  setReenterPasswordChanged: (boolean) => void;
  showSecretAnswer: boolean;
  setShowSecretAnswer: (boolean) => void;
  handleClickShowSecretAnswer: () => void;
  handleMouseDownSecretAnswer: () => void;
  passwordFocus: boolean;
  setPasswordFocus: (boolean) => void;
  showPassword: boolean;
  setShowPassword: (boolean) => void;
  handleClickShowPassword: () => void;
  handleMouseDownPassword: () => void;
  showReenterPassword: boolean;
  setShowReenterPassword: (boolean) => void;
  handleClickShowReenterPassword: () => void;
  handleMouseDownReenterPassword: () => void;
  clearSecretQuestionFields: () => void;

  emailAddress: string;
  setEmailAddress: (string) => void;
  password: string;
  handleUpdatePassword: (string) => void;
  reenterPassword: string;
  handleUpdateReenterPassword: (string) => void;
  certified: boolean;
  setCertified: (boolean) => void;
  hashKey: string;
  setHashKey: (string) => void;
  questionField: string;
  setQuestionField: (string) => void;
  secretQuestion: string;
  setSecretQuestion: (string) => void;
  secretQuestionPlaceholder: string;
  setSecretQuestionPlaceholder: (string) => void;
  secretAnswer: string;
  setSecretAnswer: (string) => void;
  secretAnswerDate: Dayjs;
  setSecretAnswerDate: (Dayjs) => void;
};

const ResetPasswordContext = createContext<resetPasswordValue | null>(null);

const useResetPasswordContext = () => {
  const resetPasswordContext = useContext(ResetPasswordContext);
  if (resetPasswordContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('resetPasswordContext must be inside a ResetPasswordProvider');
  }
  return resetPasswordContext;
};

type resetPasswordProviderProps = {
  children: ReactNode;
};

const ResetPasswordProvider = ({ children }: resetPasswordProviderProps): JSX.Element => {
  const { captureLoginAudit } = useLoginAuditContext();
  const [submitStatus, setSubmitStatus] = useState('done');
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });
  const messageRef = useRef('');

  const [loadingMessage, setLoadingMessage] = useState('');
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [passwordSecretQuestionModalOpen, setPasswordSecretQuestionModalOpen] = useState(false);
  const [emailAddressError, setEmailAddressError] = useState(false);
  const [emailAddressErrorMsg, setEmailAddressErrorMsg] = useState('');
  const [secretAnswerError, setSecretAnswerError] = useState(false);
  const [secretAnswerErrorMsg, setSecretAnswerErrorMsg] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMsg, setPasswordErrorMsg] = useState('');
  const [reenterPasswordError, setReenterPasswordError] = useState(false);
  const [reenterPasswordErrorMsg, setReenterPasswordErrorMsg] = useState('');
  const [reenterPasswordChanged, setReenterPasswordChanged] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [showSecretAnswer, setShowSecretAnswer] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [certified, setCertified] = useState(false);
  const [hashKey, setHashKey] = useState('');
  const [questionField, setQuestionField] = useState('');
  const [secretQuestion, setSecretQuestion] = useState('');
  const [secretQuestionPlaceholder, setSecretQuestionPlaceholder] = useState('');
  const [secretAnswer, setSecretAnswer] = useState('');
  const [secretAnswerDate, setSecretAnswerDate] = useState(dayjs(''));
  const [password, setPassword] = useState('');
  const [reenterPassword, setReenterPassword] = useState('');

  const secretAnswerRef = useRef('');

  const status = useMemo(() => {
    const statuses = [submitStatus];
    if (statuses.includes('error')) {
      return 'error';
    }
    if (statuses.includes('loading')) {
      return 'loading';
    }
    return 'done';
  }, [submitStatus]);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const handleClickShowReenterPassword = () => setShowReenterPassword(!showReenterPassword);
  const handleMouseDownReenterPassword = () => setShowReenterPassword(!showReenterPassword);

  const handleClickShowSecretAnswer = () => setShowSecretAnswer(!showSecretAnswer);
  const handleMouseDownSecretAnswer = () => setShowSecretAnswer(!showSecretAnswer);

  const clearSecretQuestionFields = () => {
    setSecretAnswer('');
    setSecretAnswerDate(dayjs(''));
    setSecretAnswerError(false);
    setPassword('');
    setPasswordError(false);
    setReenterPassword('');
    setReenterPasswordError(false);
    setReenterPasswordChanged(false);
  };

  const handleUpdatePassword = (newPassword: string) => {
    setPassword(() => {
      if (newPassword.length > 20) {
        setStatusAlert({
          status: 'error',
          message: 'Password is too long. Max 20 characters.',
        });
      } else {
        setStatusAlert({
          status: '',
          message: '',
        });
      }
      return newPassword.substring(0, 20);
    });
  };

  const handleUpdateReenterPassword = (newPassword: string) => {
    setReenterPassword(() => {
      if (newPassword.length > 20) {
        setStatusAlert({
          status: 'error',
          message: 'Reenter Password is too long. Max 20 characters.',
        });
      } else {
        setStatusAlert({
          status: '',
          message: '',
        });
      }
      return newPassword.substring(0, 20);
    });
  };

  // API-related section
  const { reset } = useAbortController();

  const resetPasswordUrl = process.env.REACT_APP_PUBLIC_WEB_USER_RESET_PASSWORD;

  const loadSecretQuestion = useCallback(async (personalField: string) => {
    setQuestionField(personalField);
    switch (personalField) {
      case 'lastName': {
        setSecretQuestion('Your Last Name *');
        setSecretQuestionPlaceholder('Your Last Name *');
        break;
      }
      case 'dob': {
        setSecretQuestion('Your Date of Birth');
        setSecretQuestionPlaceholder('MM/DD/YYYY');
        break;
      }
      case 'loanNo': {
        setSecretQuestion('Most recent Loan Number');
        setSecretQuestionPlaceholder('Most recent Loan Number');
        break;
      }
      default: {
        setSecretQuestion('');
        setSecretQuestionPlaceholder('');
      }
    }
  }, []);

  const resetPasswordRequest = useCallback(async () => {
    let success: boolean = false;
    if (!emailAddress) {
      messageRef.current = 'Email is not provided.';
    }

    const controller = reset();
    const config = { signal: controller.signal };

    const formData = {
      email: emailAddress,
    };

    try {
      const response = await axiosCall({ url: resetPasswordUrl, method: 'post', data: formData }, config, {
        'Content-Type': 'application/json',
      });

      if (response.secretQuestion) {
        success = true;
        loadSecretQuestion(response.secretQuestion);
        setHashKey(response.hashKey);
      } else {
        console.error(response.error);
        messageRef.current = response.error;
      }
    } catch (error) {
      console.error('Unable to reset password: ' + error);
      messageRef.current = 'Unable to reset password.';
    }

    return success;
  }, [reset, resetPasswordUrl, emailAddress, loadSecretQuestion]);

  const passwordSecretAnswerRequest = useCallback(async () => {
    let success: boolean = false;
    messageRef.current = '';
    if (!hashKey) {
      messageRef.current = 'Hash Key is not provided.';
      return success;
    } else if (!secretAnswerRef.current && secretQuestion !== 'emailAddress') {
      messageRef.current = 'Secret Answer is not provided.';
      return success;
    } else if (!password) {
      messageRef.current = 'New Password is not provided.';
      return success;
    }

    const controller = reset();
    const config = { signal: controller.signal };

    const formData = {
      hashKey: hashKey,
      secretAnswer: secretAnswerRef.current,
      newPassword: password,
    };

    try {
      const response = await axiosCall({ url: resetPasswordUrl, method: 'post', data: formData }, config, {
        'Content-Type': 'application/json',
      });

      if (response.success) {
        success = true;
      } else {
        console.error(response.Error);
        messageRef.current = response.Error;
      }
    } catch (error) {
      console.error('Unable to reset password: ' + error);
      messageRef.current = 'Unable to reset password.';
    }

    return success;
  }, [reset, resetPasswordUrl, hashKey, secretQuestion, secretAnswerRef, password]);

  // end API-related section

  const navigate = useNavigate();

  const handleFirstSuccess = () => {
    setSubmitStatus('done');
    setLoadingMessage('');
    setPasswordSecretQuestionModalOpen(true);
  };

  const handleSuccess = useCallback(() => {
    setSubmitStatus('loading');
    setLoadingMessage('Redirecting to Login...');
    setStatusAlert({ status: 'success', message: 'Successfully reset password! You will be redirected shortly.' });

    // Clear modal fields so that the User must input them again if they re-attempt
    clearSecretQuestionFields();
    setTimeout(() => {
      navigate('/login');
    }, 3000);
  }, [navigate]);

  const handleError = useCallback((type: string) => {
    setSubmitStatus('error');
    setLoadingMessage('');
    switch (type) {
      case 'There is no Web User Account with this email address.': {
        setStatusAlert({ status: 'error', message: 'An account for the provided email does not exist.' });
        break;
      }
      case 'Reset password link is expired.': {
        setStatusAlert({ status: 'error', message: 'The Reset Password session has expired. Please retry.' });
        break;
      }
      case 'Unable to validate the authenticity of this request': {
        setStatusAlert({ status: 'error', message: 'The Secret Answer was incorrect.' });
        break;
      }
      case 'The new password can not be the same as the current password.': {
        setStatusAlert({ status: 'error', message: 'Your new password cannot be the same as your current password.' });
        break;
      }
      default:
        setStatusAlert({
          status: 'error',
          message: 'The Password could not be reset. Please contact Technical Support.',
        });
    }
    // Clear modal fields so that the User must input them again if they re-attempt
    clearSecretQuestionFields();
  }, []);

  const handleFirstSubmit = useCallback(async () => {
    setSubmitStatus('loading');
    setLoadingMessage('Sending Request...');

    try {
      let success: boolean = await resetPasswordRequest();

      // if API was successful and got a response
      if (success) {
        await captureLoginAudit({
          eventType: 'PASSRESREQ',
          eventResult: 'SUCCESS',
          emailAddress,
          webUserId: '',
        });

        handleFirstSuccess();
      } else {
        await captureLoginAudit({
          eventType: 'PASSRESREQ',
          eventResult: 'FAIL',
          failureReason: 'UNKEMAIL',
          emailAddress,
          webUserId: '',
        });

        // output the error in console for STS
        console.error(messageRef.current);
        handleError(messageRef.current);
      }
    } catch (e) {
      await captureLoginAudit({
        eventType: 'PASSRESREQ',
        eventResult: 'FAIL',
        failureReason: 'AUTHERR',
        emailAddress,
        webUserId: '',
      });
      // output the error in console for STS
      console.error(e);
      handleError('other');
    }
  }, [messageRef, resetPasswordRequest, handleError]);

  const handleSecondSubmit = useCallback(async () => {
    // Password is validated, close the modal and display loading modal
    setPasswordSecretQuestionModalOpen(false);
    setSubmitStatus('loading');
    setLoadingMessage('Sending Request...');

    // if Date of Birth question, update secret answer to be the Date as a string
    if (questionField === 'dob') {
      secretAnswerRef.current = secretAnswerDate.format('MM/DD/YYYY');
    } else if (questionField === 'emailAddress') {
      secretAnswerRef.current = emailAddress;
    } else {
      secretAnswerRef.current = secretAnswer;
    }

    try {
      let success: boolean = await passwordSecretAnswerRequest();
      setSubmitStatus('done');
      setLoadingMessage('');

      if (success) {
        await captureLoginAudit({
          eventType: 'PASSRESCMP',
          eventResult: 'SUCCESS',
          emailAddress,
          webUserId: '',
        });

        handleSuccess();
      } else {
        await captureLoginAudit({
          eventType: 'PASSRESREQ',
          eventResult: 'FAIL',
          failureReason: 'UNKEMAIL',
          emailAddress,
          webUserId: '',
        });

        // output the error in console for STS
        console.error(messageRef.current);
        handleError(messageRef.current);
      }
    } catch (e) {
      // output the error in console for STS
      console.error(e);
      handleError('other');
    }
  }, [
    messageRef,
    questionField,
    emailAddress,
    secretAnswerDate,
    secretAnswer,
    secretAnswerRef,
    captureLoginAudit,
    passwordSecretAnswerRequest,
    handleSuccess,
    handleError,
  ]);

  const validateFields = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();

      let isError = false;

      if (!isValidEmailAddress(emailAddress)) {
        setEmailAddressError(true);
        setEmailAddressErrorMsg(
          isStrEmptyOrNull(emailAddress) ? 'Please enter your Email Address.' : 'Email Address provided is not valid.',
        );
        isError = true;
      } else {
        setEmailAddressError(false);
        setEmailAddressErrorMsg(' ');
      }

      if (!certified) isError = true;

      if (isError) {
        return;
      } else {
        // No Validation Errors, call handleFirstSubmit()
        handleFirstSubmit();
      }
    },
    [emailAddress, certified, handleFirstSubmit],
  );

  const validateSecretQuestionFields = useCallback(() => {
    let isError = false;

    // Check that secretAnswerDate is not valid
    if (questionField === 'dob' && !secretAnswerDate.isValid()) {
      setSecretAnswerError(true);
      setSecretAnswerErrorMsg('Please input your Date of Birth.');
      isError = true;
    } else if (questionField === 'loanNo' && !isValidLoanNo(secretAnswer)) {
      setSecretAnswerError(true);
      setSecretAnswerErrorMsg(
        isStrEmptyOrNull(secretAnswer) ? 'Please input your Loan Number.' : 'Loan Number must be 12 digits.',
      );
      isError = true;
    } else if (questionField === 'lastName' && isStrEmptyOrNull(secretAnswer)) {
      setSecretAnswerError(true);
      setSecretAnswerErrorMsg('Please input your Last Name.');
      isError = true;
    } else {
      setSecretAnswerError(false);
      setSecretAnswerErrorMsg(' ');
    }

    if (!isValidPassword(password)) {
      setPasswordError(true);
      setPasswordErrorMsg(
        isStrEmptyOrNull(password) ? 'Please enter your new Password.' : 'Password does not meet criteria.',
      );
      isError = true;
    } else {
      setPasswordError(false);
      setPasswordErrorMsg(' ');
    }

    if (!isValidReenterField(password, reenterPassword)) {
      setReenterPasswordError(true);
      setReenterPasswordErrorMsg(
        isStrEmptyOrNull(reenterPassword) ? 'Please re-enter your new Password.' : 'Passwords do not match.',
      );
      setReenterPasswordChanged(true);
      isError = true;
    } else {
      setReenterPasswordError(false);
      setReenterPasswordErrorMsg(' ');
    }

    if (isError) {
      return;
    } else {
      // No Validation Errors, call handleSecondSubmit()
      handleSecondSubmit();
    }
  }, [questionField, secretAnswer, secretAnswerDate, password, reenterPassword, handleSecondSubmit]);

  return (
    <ResetPasswordContext.Provider
      value={{
        status,
        statusAlert,
        setStatusAlert,
        loadingMessage,
        handleFirstSubmit,
        handleFirstSuccess,
        handleSuccess,
        handleError,
        validateFields,
        validateSecretQuestionFields,

        resetPasswordModalOpen,
        setResetPasswordModalOpen,
        passwordSecretQuestionModalOpen,
        setPasswordSecretQuestionModalOpen,
        emailAddressError,
        setEmailAddressError,
        emailAddressErrorMsg,
        setEmailAddressErrorMsg,
        secretAnswerError,
        setSecretAnswerError,
        secretAnswerErrorMsg,
        setSecretAnswerErrorMsg,
        passwordError,
        setPasswordError,
        passwordErrorMsg,
        setPasswordErrorMsg,
        reenterPasswordError,
        setReenterPasswordError,
        reenterPasswordErrorMsg,
        setReenterPasswordErrorMsg,
        reenterPasswordChanged,
        setReenterPasswordChanged,
        passwordFocus,
        setPasswordFocus,
        showSecretAnswer,
        setShowSecretAnswer,
        handleClickShowSecretAnswer,
        handleMouseDownSecretAnswer,
        showPassword,
        setShowPassword,
        handleClickShowPassword,
        handleMouseDownPassword,
        showReenterPassword,
        setShowReenterPassword,
        handleClickShowReenterPassword,
        handleMouseDownReenterPassword,
        clearSecretQuestionFields,

        emailAddress,
        setEmailAddress,
        password,
        handleUpdatePassword,
        reenterPassword,
        handleUpdateReenterPassword,
        certified,
        setCertified,
        hashKey,
        setHashKey,
        questionField,
        setQuestionField,
        secretQuestion,
        setSecretQuestion,
        secretQuestionPlaceholder,
        setSecretQuestionPlaceholder,
        secretAnswer,
        setSecretAnswer,
        secretAnswerDate,
        setSecretAnswerDate,
      }}
    >
      {children}
    </ResetPasswordContext.Provider>
  );
};

export { ResetPasswordProvider, useResetPasswordContext };
